import { useId, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { MockBadge } from '../components/MockBadge';
import { StepUpCodeField } from '../components/StepUpCodeField';
import { apiFetch } from '../lib/api';
import { formatDateTime, formatDateWithYear } from '../lib/format';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import {
  STEP_UP_ENDED_MESSAGE,
  isStepUpRequired,
  stepUpBody,
  stepUpCodeReady,
  useStepUpWindow,
} from '../lib/stepUp';
import { PasskeysUnsupportedError, addPasskey, isPasskeyCancelled } from '../lib/passkeys';
import { ApiError, type PasskeySummary } from '../types/api';

const MAX_PASSKEYS = 10;
const NAME_MAX = 60;
const PASSKEYS_KEY = ['auth', 'passkeys'] as const;

function stepUpErrorText(err: unknown, fallback: string): string {
  if (isTotpLocked(err)) return TOTP_LOCKED_MESSAGE;
  if (isStepUpRequired(err)) return STEP_UP_ENDED_MESSAGE;
  if (err instanceof ApiError && err.status === 401)
    return 'The code is incorrect or already used.';
  return fallback;
}

function addErrorText(err: unknown): string {
  if (err instanceof PasskeysUnsupportedError) return 'This browser does not support passkeys.';
  if (isPasskeyCancelled(err)) return 'The passkey was not created. The prompt was cancelled.';
  if (err instanceof ApiError && err.body?.error === 'passkey_limit_reached') {
    return `You already have ${MAX_PASSKEYS} passkeys. Delete one first.`;
  }
  if (err instanceof ApiError && err.body?.error === 'passkey_invalid') {
    return 'The passkey could not be verified. Try again.';
  }
  return stepUpErrorText(err, 'The passkey could not be added. Try again.');
}

function AddPasskeyCard({ count }: { count: number }) {
  const queryClient = useQueryClient();
  const nameId = useId();
  const codeId = useId();
  const stepUp = useStepUpWindow();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const atLimit = count >= MAX_PASSKEYS;

  const add = useMutation({
    mutationFn: () =>
      addPasskey({ name: name.trim(), stepUp: stepUp.active ? {} : stepUpBody(code) }),
    onSuccess: () => {
      setName('');
      setCode('');
      stepUp.onStepUpSuccess();
      void queryClient.invalidateQueries({ queryKey: PASSKEYS_KEY });
    },
    onError: stepUp.onStepUpError,
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!stepUp.active && !stepUpCodeReady(code)) return;
    add.mutate();
  }

  return (
    <Card className="p-4 flex flex-col gap-3">
      <h2 className="text-sm font-semibold m-0">Add a passkey</h2>
      <p className="m-0 text-sm text-muted">
        A passkey lets you skip the password with Touch ID, Face ID, Windows Hello or a security
        key. You still enter the code from your authenticator app after it. Adding one needs a code
        from the authenticator app.
      </p>
      {atLimit ? (
        <Alert>
          You have {MAX_PASSKEYS} passkeys, the most allowed. Delete one to add another.
        </Alert>
      ) : (
        <form className="flex flex-wrap items-end gap-3" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-1.5 min-w-[200px] flex-1">
            <Label htmlFor={nameId}>Name</Label>
            <Input
              id={nameId}
              autoComplete="off"
              maxLength={NAME_MAX}
              placeholder="e.g. Work laptop"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <StepUpCodeField
            id={codeId}
            value={code}
            onChange={setCode}
            active={stepUp.active}
            minutesLeft={stepUp.minutesLeft}
            className="flex flex-col gap-1.5 min-w-[160px]"
          />
          <Button type="submit" disabled={add.isPending}>
            {add.isPending ? 'Waiting for the passkey…' : 'Add passkey'}
          </Button>
        </form>
      )}
      {add.isError ? <Alert>{addErrorText(add.error)}</Alert> : null}
      {add.isSuccess ? (
        <p className="m-0 text-sm text-muted" role="status">
          Passkey added: {add.data.name}.
        </p>
      ) : null}
    </Card>
  );
}

function PasskeyRow({ passkey }: { passkey: PasskeySummary }) {
  const queryClient = useQueryClient();
  const codeId = useId();
  const renameId = useId();
  const stepUp = useStepUpWindow();
  const [mode, setMode] = useState<'view' | 'rename' | 'delete'>('view');
  const [name, setName] = useState(passkey.name);
  const [code, setCode] = useState('');

  const refresh = () => void queryClient.invalidateQueries({ queryKey: PASSKEYS_KEY });

  const rename = useMutation({
    mutationFn: () =>
      apiFetch(`/auth/passkeys/${encodeURIComponent(passkey.id)}`, {
        method: 'PATCH',
        body: { name: name.trim() },
      }),
    onSuccess: () => {
      setMode('view');
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: () =>
      apiFetch(`/auth/passkeys/${encodeURIComponent(passkey.id)}`, {
        method: 'DELETE',
        body: stepUp.active ? {} : stepUpBody(code),
      }),
    onSuccess: () => {
      stepUp.onStepUpSuccess();
      refresh();
    },
    onError: stepUp.onStepUpError,
  });

  return (
    <li className="py-3 flex flex-col gap-2 border-b border-border last:border-0">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
        <div className="flex flex-col min-w-0 flex-1">
          <span className="font-medium break-words">{passkey.name}</span>
          <span className="text-xs text-muted">
            Added {formatDateWithYear(passkey.createdAt)} · Last used{' '}
            {passkey.lastUsedAt === null ? 'never' : formatDateTime(passkey.lastUsedAt)}
            {passkey.backedUp ? ' · Synced' : ''}
          </span>
        </div>
        {mode === 'view' ? (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              aria-label={`Rename ${passkey.name}`}
              onClick={() => {
                setName(passkey.name);
                setMode('rename');
              }}
            >
              Rename
            </Button>
            <Button
              variant="secondary"
              size="sm"
              aria-label={`Delete ${passkey.name}`}
              onClick={() => setMode('delete')}
            >
              Delete
            </Button>
          </div>
        ) : null}
      </div>

      {mode === 'rename' ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) rename.mutate();
          }}
        >
          <div className="flex flex-col gap-1.5 min-w-[180px] flex-1">
            <Label htmlFor={renameId}>New name</Label>
            <Input
              id={renameId}
              autoComplete="off"
              maxLength={NAME_MAX}
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <Button variant="secondary" size="sm" onClick={() => setMode('view')}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={rename.isPending}>
            {rename.isPending ? 'Saving…' : 'Save'}
          </Button>
          {rename.isError ? (
            <span className="w-full text-xs text-danger-text">Could not rename. Try again.</span>
          ) : null}
        </form>
      ) : null}

      {mode === 'delete' ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!stepUp.active && !stepUpCodeReady(code)) return;
            remove.mutate();
          }}
        >
          <span className="text-sm">
            Delete "{passkey.name}"? It can no longer be used to sign in.
          </span>
          <StepUpCodeField
            id={codeId}
            value={code}
            onChange={setCode}
            active={stepUp.active}
            minutesLeft={stepUp.minutesLeft}
          />
          {remove.isError ? (
            <span className="text-xs text-danger-text">
              {stepUpErrorText(remove.error, 'Could not delete. Try again.')}
            </span>
          ) : null}
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setMode('view')}
              disabled={remove.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="danger"
              size="sm"
              aria-label={`Confirm: delete ${passkey.name}`}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Deleting…' : 'Confirm: delete'}
            </Button>
          </div>
        </form>
      ) : null}
    </li>
  );
}

/** `/indstillinger/sikkerhed` — the caller's own sign-in settings: passkeys. */
export function SecurityPage() {
  const passkeys = useQuery({
    queryKey: PASSKEYS_KEY,
    queryFn: () => apiFetch<{ passkeys: PasskeySummary[] }>('/auth/passkeys'),
    retry: false,
  });
  const notAvailable = passkeys.error instanceof ApiError && passkeys.error.status === 404;
  const list = passkeys.data?.passkeys ?? [];

  return (
    <>
      <header className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">Security</h1>
        <MockBadge />
      </header>

      {notAvailable ? (
        <Card className="p-4 flex flex-col gap-2">
          <p className="m-0 text-sm text-muted">Passkeys are not available here.</p>
        </Card>
      ) : (
        <>
          <AddPasskeyCard count={list.length} />
          <Card className="p-4 flex flex-col gap-1">
            <h2 className="text-sm font-semibold m-0">Passkeys</h2>
            {passkeys.isLoading ? (
              <div className="text-muted text-sm">Loading passkeys…</div>
            ) : null}
            {passkeys.isError && !notAvailable ? (
              <Alert>The passkeys could not be loaded.</Alert>
            ) : null}
            {passkeys.data ? (
              list.length === 0 ? (
                <div className="text-muted text-sm">
                  No passkeys yet. You sign in with your email, password and authenticator code.
                </div>
              ) : (
                <ul className="m-0 p-0 list-none">
                  {list.map((p) => (
                    <PasskeyRow key={p.id} passkey={p} />
                  ))}
                </ul>
              )
            ) : null}
          </Card>
        </>
      )}
    </>
  );
}
